/**
 * The contract of the Music24 realtime event channel (music24-api -> the web app).
 *
 * An event is a hint. It says "this thing changed" and holds ids only. The client reads the data
 * again through the REST and tRPC routes, which check the session. Never put a title, a caption,
 * a number, a file address or a status text in an event.
 *
 * The server sends each event to the room of one workspace (licence). The server puts a socket
 * into its room from the session token. A client cannot ask for a room.
 */

/** Raise it when the shape of an event changes in a way an old client cannot read. */
export const REALTIME_PROTOCOL_VERSION = 1;

/** The Engine.IO path on music24-api. A browser adds its API prefix: `/api_api/realtime/`. */
export const REALTIME_SOCKET_PATH = '/realtime/';

/** The Socket.IO event name of every realtime event. The payload is one `RealtimeEvent`. */
export const REALTIME_EVENT_NAME = 'm24.event';

/** The platforms of a Promote channel (the Prisma enum `SocialPlatform` of music24-api). */
export const realtimeSocialPlatforms = ['tiktok', 'instagram', 'youtube', 'facebook', 'threads', 'x'] as const;
export type RealtimeSocialPlatform = (typeof realtimeSocialPlatforms)[number];

/** A read of a channel ended (ok, failed or "needs reconnect"), or the channel connected or disconnected. */
export interface PromoChannelSyncedEvent {
  type: 'promo.channel.synced';
  accountId: number;
  platform: RealtimeSocialPlatform;
}

/**
 * One scheduled post changed: it was made, edited (caption, time, cancel, schedule), moved or
 * deleted, or it has a new status (publishing, posted, failed, waiting for a manual post).
 */
export interface PromoPostUpdatedEvent {
  type: 'promo.post.updated';
  accountId: number;
  postUuid: string;
}

/**
 * Many posts of the account changed in one request (a bulk add: the Composer to many channels, the
 * rollout drafts of the Plan tab, "Schedule all drafts of this day"). The client reads the calendar again.
 */
export interface PromoPostsChangedEvent {
  type: 'promo.posts.changed';
  accountId: number;
}

/** An AI reel has a new status. `catalogItemUuid` is the song of the reel, when it has one. */
export interface ReelUpdatedEvent {
  type: 'reel.updated';
  accountId: number;
  reelUuid: string;
  catalogItemUuid: string | null;
}

/** A Release Kit asset has a new status or a new render progress. */
export interface KitAssetUpdatedEvent {
  type: 'kit.asset.updated';
  accountId: number;
  catalogItemUuid: string;
  assetUuid: string;
}

/**
 * The song files changed: a step of the song master ended (the AAC copy is stored and the player
 * can play, the song analysis is stored, or the master failed), the lyrics changed, the lyric
 * timing changed (a sync started, ended or failed, or a member edited a line time), the cover
 * of the row has a stored file now, or the kit of the song has the colours of a new cover.
 */
export interface CatalogSongUpdatedEvent {
  type: 'catalog.song.updated';
  accountId: number;
  catalogItemUuid: string;
}

/**
 * The zip of one "Download all" request is ready or failed. `requestId` is the id that the
 * 202 answer of `POST .../zip` gave. The client reads `GET .../zip/:requestId` for the link.
 */
export interface KitZipUpdatedEvent {
  type: 'kit.zip.updated';
  accountId: number;
  catalogItemUuid: string;
  requestId: string;
}

/**
 * A read of an artist portal or a distributor (Spotify for Artists, Deezer for Creators, YouTube,
 * Ditto Music) started or ended (ok, failed, needs reconnect). The Streaming screen, the Catalog
 * cards and the navigation badge read their numbers again.
 */
export interface ArtistPortalSyncedEvent {
  type: 'artistPortal.synced';
  accountId: number;
  /** The `integrationType` of the connection: `spotify`, `deezer`, `youtubeMusic`, `ditto`. */
  integrationType: string;
}

/**
 * An AI picture of the cover editor has a new status (queued, working, ready, failed). The editor
 * reads `GET /accounts/:id/cover-editor/:catalogItemUuid` again.
 */
export interface CoverPictureUpdatedEvent {
  type: 'cover.picture.updated';
  accountId: number;
  catalogItemUuid: string;
  runUuid: string;
}

/** A one-off publish record (YouTube, Spotify Canvas, Facebook, Instagram) changed its state. */
export interface AssetPublishUpdatedEvent {
  type: 'asset.publish.updated';
  accountId: number;
  catalogItemUuid: string;
  assetUuid: string;
  publishUuid: string;
}

/**
 * A song publish record (SoundCloud first; `music24/songPublish.ts`) changed its state. The web
 * reads `GET /accounts/:accountId/song-publishes` again (tag `SongPublish`).
 */
export interface SongPublishUpdatedEvent {
  type: 'song.publish.updated';
  accountId: number;
  catalogItemUuid: string;
  publishUuid: string;
}

/** A subscriber release changed (deliveries written, saved, emailed). Ids only. */
export interface PresaveReleaseUpdatedEvent {
  type: 'presave.release.updated';
  accountId: number;
  releaseUuid: string;
  catalogItemUuid: string | null;
}

/**
 * A cut of the music video editor changed: the director wrote it (a first cut or a recut ended or
 * failed), or a member saved it. The editor reads `GET .../music-video/:catalogItemUuid/cuts/:cutUuid` again.
 */
export interface MusicVideoCutUpdatedEvent {
  type: 'musicVideo.cut.updated';
  accountId: number;
  catalogItemUuid: string;
  cutUuid: string;
}

/** A footage clip of the account has a new status (an AI clip is made or failed, an upload is ready, a clip is archived). */
export interface FootageClipUpdatedEvent {
  type: 'footage.clip.updated';
  accountId: number;
  clipUuid: string;
}

/** The Outreach sound profile of an artist was built, failed or changed. */
export interface OutreachProfileUpdatedEvent {
  type: 'outreach.profile.updated';
  accountId: number;
  profileUuid: string;
}

/** An Outreach campaign changed: its status, its settings, its numbers or its messages as a whole. */
export interface OutreachCampaignUpdatedEvent {
  type: 'outreach.campaign.updated';
  accountId: number;
  campaignUuid: string;
}

/** One Outreach message changed (written, edited, approved, sent, bounced, skipped). */
export interface OutreachMessageUpdatedEvent {
  type: 'outreach.message.updated';
  accountId: number;
  campaignUuid: string;
  messageUuid: string;
}

/** A reply to an Outreach message arrived or got its label. */
export interface OutreachReplyReceivedEvent {
  type: 'outreach.reply.received';
  accountId: number;
  campaignUuid: string;
  replyUuid: string;
}

/** Music24 found a placement of a campaign song (detected, from a reply, or added by hand). */
export interface OutreachPlacementFoundEvent {
  type: 'outreach.placement.found';
  accountId: number;
  campaignUuid: string;
  placementUuid: string;
}

/**
 * An Outreach mailbox changed (health, cap, connect, remove). A mailbox belongs to the workspace:
 * the server sends the event to every artist of the licence (`emitToLicence`).
 */
export interface OutreachMailboxUpdatedEvent {
  type: 'outreach.mailbox.updated';
  accountId: number;
  mailboxUuid: string;
}

/**
 * The target find of an artist made progress (playlists scanned / total). The server sends it while
 * a find runs, at most once every 2 s for one artist, and once when the find ends. The clients read
 * `outreach.campaigns.live` and `outreach.home` again.
 */
export interface OutreachFindProgressEvent {
  type: 'outreach.find.progress';
  accountId: number;
}

/**
 * A directed video (the beta of the music video editor) changed: status, director output, cast,
 * a settled clip or the render progress. The clients read the video and the list of the song again.
 */
export interface DirectedVideoUpdatedEvent {
  type: 'musicVideo.directed.updated';
  accountId: number;
  catalogItemUuid: string;
  directedVideoUuid: string;
}

/**
 * A version of an artist Profile image changed (queued, rendering, ready, failed, a Spotify upload
 * state, the manual mark). The clients read the Profile again (`GET .../artist-profile`).
 */
export interface ProfileAssetUpdatedEvent {
  type: 'profile.asset.updated';
  accountId: number;
  assetUuid: string;
  kind: string;
}

/**
 * A Profile "Send to all" changed: made, an item sending, live, failed. The clients read the send
 * again (`GET .../artist-profile/send/:sendUuid`) and the sheet (`GET .../artist-profile/send`).
 */
export interface ProfileSendUpdatedEvent {
  type: 'profile.send.updated';
  accountId: number;
  sendUuid: string;
}

export type RealtimeEvent =
  | PromoChannelSyncedEvent
  | ArtistPortalSyncedEvent
  | PromoPostUpdatedEvent
  | PromoPostsChangedEvent
  | ReelUpdatedEvent
  | KitAssetUpdatedEvent
  | CatalogSongUpdatedEvent
  | KitZipUpdatedEvent
  | CoverPictureUpdatedEvent
  | AssetPublishUpdatedEvent
  | SongPublishUpdatedEvent
  | PresaveReleaseUpdatedEvent
  | MusicVideoCutUpdatedEvent
  | DirectedVideoUpdatedEvent
  | FootageClipUpdatedEvent
  | OutreachProfileUpdatedEvent
  | OutreachCampaignUpdatedEvent
  | OutreachMessageUpdatedEvent
  | OutreachReplyReceivedEvent
  | OutreachPlacementFoundEvent
  | OutreachMailboxUpdatedEvent
  | OutreachFindProgressEvent
  | ProfileAssetUpdatedEvent
  | ProfileSendUpdatedEvent;

export type RealtimeEventType = RealtimeEvent['type'];

export const realtimeEventTypes: readonly RealtimeEventType[] = [
  'promo.channel.synced',
  'promo.post.updated',
  'promo.posts.changed',
  'reel.updated',
  'kit.asset.updated',
  'catalog.song.updated',
  'kit.zip.updated',
  'artistPortal.synced',
  'cover.picture.updated',
  'asset.publish.updated',
  'song.publish.updated',
  'presave.release.updated',
  'musicVideo.cut.updated',
  'musicVideo.directed.updated',
  'footage.clip.updated',
  'outreach.profile.updated',
  'outreach.campaign.updated',
  'outreach.message.updated',
  'outreach.reply.received',
  'outreach.placement.found',
  'outreach.mailbox.updated',
  'outreach.find.progress',
  'profile.asset.updated',
  'profile.send.updated',
];

/** The event of one type, for a typed handler. */
export type RealtimeEventOfType<T extends RealtimeEventType> = Extract<RealtimeEvent, { type: T }>;

/**
 * The room of one workspace. Only the server calls it, with a licence id from the database or
 * from a checked session token.
 */
export function realtimeLicenceRoom(licenseId: number): string {
  return `licence:${licenseId}`;
}

/** What the client sends in the Socket.IO `auth` of the handshake. */
export interface RealtimeHandshakeAuth {
  /** The Music24 session token. An API token (`m24_`, `m24at_`) is refused. */
  token: string;
  /** `REALTIME_PROTOCOL_VERSION` of the client. */
  v: number;
}

/**
 * Why the server refused a handshake. It is the `message` of the Socket.IO `connect_error`.
 * `expired` and `unauthorized` mean "refresh the session token, then connect again".
 */
export const realtimeHandshakeErrors = ['unauthorized', 'expired', 'no_workspace', 'unavailable'] as const;
export type RealtimeHandshakeError = (typeof realtimeHandshakeErrors)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

/**
 * True when `value` is an event of this contract. Both sides use it on what comes off the wire:
 * the server on a Redis message, the client on a socket message. An unknown type is not an event,
 * so a client ignores the events of a newer server.
 */
export function isRealtimeEvent(value: unknown): value is RealtimeEvent {
  if (!isRecord(value) || !isId(value.accountId)) {
    return false;
  }
  switch (value.type) {
    case 'promo.channel.synced':
      return (realtimeSocialPlatforms as readonly unknown[]).includes(value.platform);
    case 'promo.post.updated':
      return isText(value.postUuid);
    case 'promo.posts.changed':
      return true;
    case 'reel.updated':
      return isText(value.reelUuid) && (value.catalogItemUuid === null || isText(value.catalogItemUuid));
    case 'kit.asset.updated':
      return isText(value.catalogItemUuid) && isText(value.assetUuid);
    case 'catalog.song.updated':
      return isText(value.catalogItemUuid);
    case 'kit.zip.updated':
      return isText(value.catalogItemUuid) && isText(value.requestId);
    case 'artistPortal.synced':
      return isText(value.integrationType);
    case 'cover.picture.updated':
      return isText(value.catalogItemUuid) && isText(value.runUuid);
    case 'asset.publish.updated':
      return isText(value.catalogItemUuid) && isText(value.assetUuid) && isText(value.publishUuid);
    case 'song.publish.updated':
      return isText(value.catalogItemUuid) && isText(value.publishUuid);
    case 'presave.release.updated':
      return isText(value.releaseUuid) && (value.catalogItemUuid === null || isText(value.catalogItemUuid));
    case 'musicVideo.cut.updated':
      return isText(value.catalogItemUuid) && isText(value.cutUuid);
    case 'musicVideo.directed.updated':
      return isText(value.catalogItemUuid) && isText(value.directedVideoUuid);
    case 'footage.clip.updated':
      return isText(value.clipUuid);
    case 'outreach.profile.updated':
      return isText(value.profileUuid);
    case 'outreach.campaign.updated':
      return isText(value.campaignUuid);
    case 'outreach.message.updated':
      return isText(value.campaignUuid) && isText(value.messageUuid);
    case 'outreach.reply.received':
      return isText(value.campaignUuid) && isText(value.replyUuid);
    case 'outreach.placement.found':
      return isText(value.campaignUuid) && isText(value.placementUuid);
    case 'outreach.mailbox.updated':
      return isText(value.mailboxUuid);
    case 'outreach.find.progress':
      return true;
    case 'profile.asset.updated':
      return isText(value.assetUuid) && isText(value.kind);
    case 'profile.send.updated':
      return isText(value.sendUuid);
    default:
      return false;
  }
}
